<script setup>
import { ref, reactive, watch } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import { useStore } from 'dashboard/composables/store';

const emit = defineEmits(['close']);
const store = useStore();

const show = ref(true);
const saving = ref(false);
const errorMsg = ref('');

const STATUSES = ['Novo', 'Contatei', 'Em conversa', 'Fechado'];

const acceptsInput = ref('');

const form = reactive({
  code: '',
  type: '',
  neighborhood: '',
  city: '',
  price: '',
  phone: '',
  adUrl: '',
  accepts: [],
  conversation_id: '',
  contact_id: '',
  notes: '',
  status: 'Novo',
});

// SiteUp: origem do cadastro — "site" (puxa as infos por código) ou "avulso" (digita tudo).
const origem = ref('site');
const looking = ref(false);
const lookupMsg = ref('');

const pull = async () => {
  const code = form.code.trim();
  if (!code || looking.value) return;
  looking.value = true;
  lookupMsg.value = '';
  try {
    const data = await store.dispatch('permutas/lookup', code);
    form.type = data.type || form.type;
    form.neighborhood = data.neighborhood || form.neighborhood;
    form.city = data.city || form.city;
    form.price = data.price != null ? String(data.price) : form.price;
    form.adUrl = data.adUrl || form.adUrl;
    lookupMsg.value = `✓ ${data.type || 'Imóvel'} em ${data.neighborhood || data.city || '—'} puxado.`;
  } catch (e) {
    lookupMsg.value = e?.response?.data?.error || 'Imóvel não encontrado para esse código.';
  } finally {
    looking.value = false;
  }
};

// SiteUp: busca de contato (proprietário) por nome/telefone/email, no lugar de IDs crus.
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const searchingContact = ref(false);
let contactTimer = null;

const onContactSearch = () => {
  clearTimeout(contactTimer);
  const q = contactQuery.value.trim();
  if (q.length < 2) {
    contactResults.value = [];
    return;
  }
  contactTimer = setTimeout(async () => {
    searchingContact.value = true;
    try {
      contactResults.value = await store.dispatch('permutas/searchContacts', q);
    } catch (e) {
      contactResults.value = [];
    } finally {
      searchingContact.value = false;
    }
  }, 300);
};

const selectContact = c => {
  selectedContact.value = c;
  form.contact_id = c.id;
  form.conversation_id = c.conversation_id || '';
  contactResults.value = [];
  contactQuery.value = '';
};

const clearContact = () => {
  selectedContact.value = null;
  form.contact_id = '';
  form.conversation_id = '';
  contactQuery.value = '';
  contactResults.value = [];
};

// SiteUp: ao trocar a origem (ex.: do site → avulso), limpa tudo que já foi digitado/puxado.
const resetForm = () => {
  form.code = '';
  form.type = '';
  form.neighborhood = '';
  form.city = '';
  form.price = '';
  form.phone = '';
  form.adUrl = '';
  form.accepts = [];
  form.notes = '';
  form.status = 'Novo';
  acceptsInput.value = '';
  lookupMsg.value = '';
  errorMsg.value = '';
  clearContact();
};

watch(origem, resetForm);

const addAccepts = () => {
  const val = acceptsInput.value.trim();
  if (val && !form.accepts.includes(val)) {
    form.accepts.push(val);
  }
  acceptsInput.value = '';
};

const removeAccepts = item => {
  form.accepts = form.accepts.filter(a => a !== item);
};

const onAcceptsKeydown = e => {
  if (e.key === 'Enter' || e.key === ',') {
    e.preventDefault();
    addAccepts();
  }
};

const close = () => {
  show.value = false;
  emit('close');
};

const handleSubmit = async () => {
  if (!form.code.trim()) {
    errorMsg.value = 'O código é obrigatório.';
    return;
  }
  errorMsg.value = '';
  saving.value = true;

  try {
    const payload = {
      source: 'manual',
      code: form.code.trim(),
      status: form.status,
      conversation_id: form.conversation_id || undefined,
      contact_id: form.contact_id || undefined,
      notes: form.notes || undefined,
      payload: {
        type: form.type || undefined,
        neighborhood: form.neighborhood || undefined,
        city: form.city || undefined,
        price: form.price ? Number(form.price) : undefined,
        phone: form.phone || undefined,
        adUrl: form.adUrl || undefined,
        accepts: form.accepts,
      },
    };
    await store.dispatch('permutas/createManual', payload);
    close();
  } catch (e) {
    errorMsg.value = e?.message || 'Erro ao salvar. Tente novamente.';
  } finally {
    saving.value = false;
  }
};

const FLD =
  'w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand';
</script>

<template>
  <Modal v-model:show="show" :on-close="close" @close="close">
    <div class="flex flex-col w-full min-w-0">
      <!-- Header -->
      <div class="flex items-center px-5 py-4 border-b border-n-weak">
        <h2 class="text-base font-medium text-n-slate-12">Adicionar permuta</h2>
      </div>

      <!-- Body -->
      <div class="flex flex-col gap-5 px-5 py-5">
        <!-- Origem (segmented) -->
        <div class="flex gap-0.5 p-0.5 rounded-xl bg-n-alpha-2">
          <button
            type="button"
            class="flex-1 h-8 rounded-md text-sm font-medium transition-colors"
            :class="
              origem === 'site'
                ? 'bg-n-solid-1 text-n-slate-12'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="origem = 'site'"
          >
            Do site
          </button>
          <button
            type="button"
            class="flex-1 h-8 rounded-md text-sm font-medium transition-colors"
            :class="
              origem === 'avulso'
                ? 'bg-n-solid-1 text-n-slate-12'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="origem = 'avulso'"
          >
            Avulso
          </button>
        </div>

        <!-- Error -->
        <div
          v-if="errorMsg"
          class="rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2 text-xs text-red-500"
        >
          {{ errorMsg }}
        </div>

        <!-- Código (+ Puxar no "do site") -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium text-n-slate-11">
            {{ origem === 'site' ? 'Código do imóvel no site' : 'Código / referência' }}
            <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <input
              v-model="form.code"
              type="text"
              :placeholder="origem === 'site' ? 'Ex.: 2542' : 'Um código ou apelido'"
              :class="FLD"
              class="flex-1"
              @keydown.enter.prevent="origem === 'site' && pull()"
            />
            <button
              v-if="origem === 'site'"
              type="button"
              class="px-4 rounded-xl bg-n-brand text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
              :disabled="looking || !form.code.trim()"
              @click="pull"
            >
              {{ looking ? 'Puxando…' : 'Puxar' }}
            </button>
          </div>
          <p v-if="lookupMsg" class="text-xs text-n-teal-11">{{ lookupMsg }}</p>
        </div>

        <!-- Imóvel -->
        <div class="flex flex-col gap-2.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10">Imóvel</span>
          <div class="grid grid-cols-2 gap-2.5">
            <input v-model="form.type" type="text" placeholder="Tipo (ex.: Casa)" :class="FLD" />
            <input v-model="form.neighborhood" type="text" placeholder="Bairro" :class="FLD" />
            <input v-model="form.city" type="text" placeholder="Cidade" :class="FLD" />
            <input v-model="form.price" type="number" min="0" placeholder="Preço (R$)" :class="FLD" />
          </div>
          <input v-model="form.adUrl" type="url" placeholder="Link do anúncio (opcional)" :class="FLD" />
        </div>

        <!-- Aceita em troca -->
        <div class="flex flex-col gap-2">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10">Aceita em troca</span>
          <div
            class="flex flex-wrap gap-1.5 items-center min-h-[38px] rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 focus-within:ring-1 focus-within:ring-n-brand"
          >
            <span
              v-for="a in form.accepts"
              :key="a"
              class="inline-flex items-center gap-1 rounded-md bg-n-alpha-3 px-2 py-0.5 text-xs text-n-slate-12"
            >
              {{ a }}
              <button type="button" class="text-n-slate-10 hover:text-n-slate-12" @click="removeAccepts(a)">×</button>
            </span>
            <input
              v-model="acceptsInput"
              type="text"
              placeholder="digite e Enter…"
              class="flex-1 min-w-[100px] bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
              @keydown="onAcceptsKeydown"
              @blur="addAccepts"
            />
          </div>
        </div>

        <!-- Vínculo e status -->
        <div class="flex flex-col gap-2.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10">Vínculo e status</span>
          <div class="grid grid-cols-[1fr_8rem] gap-2.5">
            <div>
              <div
                v-if="selectedContact"
                class="flex items-center justify-between min-h-[38px] rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-1.5"
              >
                <div class="min-w-0">
                  <p class="text-sm text-n-slate-12 truncate">{{ selectedContact.name || 'Sem nome' }}</p>
                  <p class="text-xs text-n-slate-11 truncate">
                    {{ selectedContact.phone || selectedContact.email || 'sem contato' }}
                    <span v-if="selectedContact.conversation_id">· conversa #{{ selectedContact.conversation_id }}</span>
                  </p>
                </div>
                <button
                  type="button"
                  class="text-xs text-n-slate-10 hover:text-n-slate-12 flex-shrink-0 ltr:ml-2 rtl:mr-2"
                  @click="clearContact"
                >
                  trocar
                </button>
              </div>
              <div v-else class="relative">
                <input
                  v-model="contactQuery"
                  type="text"
                  placeholder="Buscar proprietário (nome/tel/e-mail)"
                  :class="FLD"
                  @input="onContactSearch"
                />
                <div
                  v-if="contactResults.length"
                  class="absolute z-10 left-0 right-0 mt-1 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg py-1 max-h-48 overflow-auto"
                >
                  <button
                    v-for="c in contactResults"
                    :key="c.id"
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-n-alpha-2 transition-colors"
                    @click="selectContact(c)"
                  >
                    <span class="text-sm text-n-slate-12">{{ c.name || 'Sem nome' }}</span>
                    <span class="block text-xs text-n-slate-11">{{ c.phone || c.email || '—' }}</span>
                  </button>
                </div>
                <p v-else-if="searchingContact" class="text-xs text-n-slate-10 mt-1">Buscando…</p>
              </div>
            </div>
            <select v-model="form.status" :class="FLD">
              <option v-for="s in STATUSES" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <input
            v-model="form.phone"
            type="text"
            placeholder="Telefone do dono (se não houver contato)"
            :class="FLD"
          />
          <textarea
            v-model="form.notes"
            rows="2"
            placeholder="Notas (opcional)"
            :class="FLD"
            class="resize-none"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2 px-5 py-4 border-t border-n-weak">
        <button
          type="button"
          class="px-4 h-9 rounded-xl border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
          :disabled="saving"
          @click="close"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="px-4 h-9 rounded-xl bg-n-brand text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          :disabled="saving"
          @click="handleSubmit"
        >
          {{ saving ? 'Salvando…' : 'Salvar permuta' }}
        </button>
      </div>
    </div>
  </Modal>
</template>
